import React, { useState, useEffect } from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonItem,
  IonLabel,
  IonButton,
  IonToast,
  IonLoading,
  IonSearchbar,
  IonChip,
  IonIcon,
  IonFab,
  IonFabButton,
  IonBadge,
  IonText,
  IonAvatar,
  IonRefresher,
  IonRefresherContent,
} from "@ionic/react";
import {
  warningOutline,
  flashOutline,
  thermometerOutline,
  wifiOutline,
  addOutline,
  timeOutline,
  eyeOutline,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { RefresherEventDetail } from "@ionic/core";
import { ApiService } from "../services/api.service";
import SubmitReport from "./SubmitReport";
import "./Tab1.css";

// Report interface
interface Report {
  id: number;
  title: string;
  description: string;
  status: "pending" | "in_progress" | "resolved" | "acknowledged";
  createdAt: string;
  imageUrl: string | null;
  totalImages: number;
  priority: string;
  issueType: string;
}

const Tab1: React.FC = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState<Report[]>([]);
  const [filteredReports, setFilteredReports] = useState<Report[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [searchText, setSearchText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastColor, setToastColor] = useState<string>("success");
  const [showSubmitReport, setShowSubmitReport] = useState<boolean>(false);

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    filterReports();
  }, [reports, selectedStatus, searchText]);

  const loadReports = async () => {
    setIsLoading(true);
    try {
      // Make API call to get user's reports
      const response = await ApiService.get("/my-reports");

      if (response.data && response.status === 200) {
        // Transform API response to match our Report interface
        const apiReports = response.data.reports || [];
        const transformedReports: Report[] = apiReports.map((report: any) => ({
          id: report.id || report._id,
          title: report.title,
          description: report.description,
          status: report.status || "pending",
          createdAt: new Date(
            report.createdAt || report.timestamp || report.created_at
          ).toLocaleDateString(),
          imageUrl:
            report.imageUrl ||
            report.image ||
            (report.images && report.images[0]) ||
            null,
          totalImages: report.images
            ? report.images.length
            : report.imageUrl
            ? 1
            : 0,
          priority: report.priority || "medium",
          issueType: report.issueType || report.category || "warning",
        }));

        setReports(transformedReports);
        console.log("Reports loaded successfully:", transformedReports.length);
      } else {
        throw new Error(response.data?.message || "Failed to load reports");
      }
    } catch (error: any) {
      console.error("Error loading reports:", error);

      // Handle specific error cases
      let errorMessage = t("reports.failedToLoad");

      if (error.response?.status === 401) {
        errorMessage = t("reports.errors.unauthorized");
      } else if (error.response?.status === 403) {
        errorMessage = t("reports.errors.forbidden");
      } else if (error.response?.status >= 500) {
        errorMessage = t("reports.errors.serverError");
      } else if (error.code === "NETWORK_ERROR" || !navigator.onLine) {
        errorMessage = t("reports.errors.networkError");
      }

      setToastMessage(errorMessage);
      setToastColor("danger");
      setShowToast(true);

      // Set empty array on error to show "no reports" message
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterReports = () => {
    let filtered = reports;

    // Filter by status
    if (selectedStatus !== "all") {
      filtered = filtered.filter((report) => report.status === selectedStatus);
    }

    // Filter by search text
    if (searchText.trim()) {
      filtered = filtered.filter(
        (report) =>
          report.title.toLowerCase().includes(searchText.toLowerCase()) ||
          report.description.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    setFilteredReports(filtered);
  };

  const getStatusCounts = () => {
    return {
      all: reports.length,
      raised: reports.filter((r) => r.status === "pending").length,
      acknowledged: reports.filter((r) => r.status === "acknowledged").length,
      in_progress: reports.filter((r) => r.status === "in_progress").length,
      resolved: reports.filter((r) => r.status === "resolved").length,
    };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "acknowledged":
        return "primary";
      case "in_progress":
        return "secondary";
      case "resolved":
        return "success";
      default:
        return "medium";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "urgent":
        return "danger";
      case "high":
        return "warning";
      case "medium":
        return "primary";
      case "low":
        return "success";
      default:
        return "medium";
    }
  };

  const getPriorityBorderClass = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "urgent":
        return "priority-urgent";
      case "high":
        return "priority-high";
      case "medium":
        return "priority-medium";
      case "low":
        return "priority-low";
      default:
        return "priority-default";
    }
  };

  const getStatusIcon = (iconType: string) => {
    switch (iconType) {
      case "warning":
        return warningOutline;
      case "flash":
        return flashOutline;
      case "thermometer":
        return thermometerOutline;
      case "wifi":
        return wifiOutline;
      default:
        return warningOutline;
    }
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await loadReports();
    event.detail.complete();
  };

  const handleSubmitReport = () => {
    setShowSubmitReport(true);
  };

  const handleSubmitReportBack = () => {
    setShowSubmitReport(false);
  };

  const handleSubmitReportSuccess = () => {
    setShowSubmitReport(false);
    setToastMessage(t("reports.reportSubmitted"));
    setToastColor("success");
    setShowToast(true);
    // Reload reports to show the new one
    loadReports();
  };

  const statusCounts = getStatusCounts();

  // Show SubmitReport component if needed
  if (showSubmitReport) {
    return (
      <SubmitReport
        onBack={handleSubmitReportBack}
        onSubmitSuccess={handleSubmitReportSuccess}
      />
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("reports.dashboard")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">{t("reports.dashboard")}</IonTitle>
          </IonToolbar>
        </IonHeader>

        {/* Search Bar */}
        <div className="search-container">
          <IonSearchbar
            value={searchText}
            onIonInput={(e) => setSearchText(e.detail.value!)}
            placeholder={t("reports.searchPlaceholder")}
            showClearButton="focus"
          />
        </div>

        {/* Status Filter Chips */}
        <div className="status-filters">
          <IonChip
            color={selectedStatus === "all" ? "primary" : "medium"}
            onClick={() => setSelectedStatus("all")}
            className="status-chip"
          >
            <IonLabel>{t("reports.allReports")}</IonLabel>
            {statusCounts.all > 0 && (
              <IonBadge color="primary">{statusCounts.all}</IonBadge>
            )}
          </IonChip>

          <IonChip
            color={selectedStatus === "pending" ? "warning" : "medium"}
            onClick={() => setSelectedStatus("pending")}
            className="status-chip"
          >
            <IonLabel>{t("reports.pending")}</IonLabel>
            {statusCounts.raised > 0 && (
              <IonBadge color="warning">{statusCounts.raised}</IonBadge>
            )}
          </IonChip>

          <IonChip
            color={selectedStatus === "acknowledged" ? "primary" : "medium"}
            onClick={() => setSelectedStatus("acknowledged")}
            className="status-chip"
          >
            <IonLabel>{t("reports.acknowledged")}</IonLabel>
            {statusCounts.acknowledged > 0 && (
              <IonBadge color="primary">{statusCounts.acknowledged}</IonBadge>
            )}
          </IonChip>

          <IonChip
            color={selectedStatus === "in_progress" ? "secondary" : "medium"}
            onClick={() => setSelectedStatus("in_progress")}
            className="status-chip"
          >
            <IonLabel>{t("reports.in_progress")}</IonLabel>
            {statusCounts.in_progress > 0 && (
              <IonBadge color="secondary">{statusCounts.in_progress}</IonBadge>
            )}
          </IonChip>

          <IonChip
            color={selectedStatus === "resolved" ? "success" : "medium"}
            onClick={() => setSelectedStatus("resolved")}
            className="status-chip"
          >
            <IonLabel>{t("reports.resolved")}</IonLabel>
            {statusCounts.resolved > 0 && (
              <IonBadge color="success">{statusCounts.resolved}</IonBadge>
            )}
          </IonChip>
        </div>

        {/* Reports List */}
        <div className="reports-container">
          {isLoading ? (
            <div className="loading-container">
              <IonText color="medium">{t("reports.loadingReports")}</IonText>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="no-reports-container">
              <IonText color="medium">{t("reports.noReportsFound")}</IonText>
            </div>
          ) : (
            filteredReports.map((report) => (
              <IonCard
                key={report.id}
                className={`report-card ${getPriorityBorderClass(
                  report.priority
                )}`}
              >
                <IonItem lines="none" className="report-item">
                  <IonAvatar slot="start" className="report-avatar">
                    {report.imageUrl ? (
                      <img
                        src={report.imageUrl}
                        alt={report.title}
                        className="report-image"
                      />
                    ) : (
                      <div className={`report-icon ${report.issueType}`}>
                        <IonIcon icon={getStatusIcon(report.issueType)} />
                      </div>
                    )}
                  </IonAvatar>

                  <IonLabel className="report-content">
                    <div className="report-header">
                      <h3 className="report-title">{report.title}</h3>
                      <div className="report-badges">
                        <IonChip
                          color={getPriorityColor(report.priority)}
                          className="priority-badge"
                        >
                          <IonLabel>
                            {t(`submitReport.priority.${report.priority}`)}
                          </IonLabel>
                        </IonChip>
                        <IonChip
                          color={getStatusColor(report.status)}
                          className="status-badge"
                        >
                          <IonLabel>{t(`reports.${report.status}`)}</IonLabel>
                        </IonChip>
                      </div>
                    </div>

                    <p className="report-description">{report.description}</p>

                    <div className="report-footer">
                      <div className="report-time">
                        <IonIcon icon={timeOutline} />
                        <span>
                          {t("reports.submitted")}: {report.createdAt}
                        </span>
                      </div>
                      <IonButton
                        fill="clear"
                        size="small"
                        color="primary"
                        className="view-details-btn"
                      >
                        <IonIcon icon={eyeOutline} slot="start" />
                        {t(`issueTypes.${report.issueType}`)}
                      </IonButton>
                    </div>
                  </IonLabel>
                </IonItem>
              </IonCard>
            ))
          )}
        </div>

        {/* Floating Action Button */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton color="primary" onClick={handleSubmitReport}>
            <IonIcon icon={addOutline} />
          </IonFabButton>
        </IonFab>

        <IonLoading isOpen={isLoading} message={t("reports.loadingReports")} />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
        />
      </IonContent>
    </IonPage>
  );
};

export default Tab1;
